import {
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type Node,
  type OnEdgesChange,
  type OnNodesChange,
  type Viewport
} from "@xyflow/react"
import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import type { NewNode } from "@/pages/ContentSteering/Segmentation/SegmentationFlow/NewSegmentNode"
import type { NodeData } from "@/pages/ContentSteering/Segmentation/SegmentationFlow/SegmentNode"

interface Properties {
  nodes: Node<NodeData | NewNode>[]
  edges: Edge[]
  currentViewport?: Viewport | null
}

interface Methods {
  setNodes: (nodes: Node<NodeData | NewNode>[]) => void
  setEdges: (edges: Edge[]) => void
  onNodesChange: OnNodesChange<Node<NodeData | NewNode>>
  onEdgesChange: OnEdgesChange
  setShowEdges: (nodeId: string, show: boolean) => void
  setCurrentViewport: (viewport: Viewport | null) => void
}

export type SegmentationFlowState = Properties & Methods

const initialState: Properties = {
  currentViewport: null,
  edges: [],
  nodes: []
}

export const useSegmentationFlowStore = create<SegmentationFlowState>()(
  persist(
    (set, get) => ({
      ...initialState,
      onEdgesChange: changes => {
        set({ edges: applyEdgeChanges(changes, get().edges) })
      },
      onNodesChange: changes => {
        set({ nodes: applyNodeChanges<Node<NodeData | NewNode>>(changes, get().nodes) })
      },
      setCurrentViewport: (viewport: Viewport | null) => {
        set({ currentViewport: viewport })
      },
      setEdges: (edges: Edge[]) => set({ edges }),
      setNodes: (nodes: Node<NodeData | NewNode>[]) => set({ nodes }),
      setShowEdges: (nodeId, show) => {
        const edges = get().edges.map(edge => {
          if (edge.target === nodeId) {
            return { ...edge, data: { ...edge.data, show } }
          }
          return edge
        })
        set({ edges })
      }
    }),
    {
      name: "segmentation-flow",
      storage: createJSONStorage(() => localStorage)
    }
  )
)
